import React, { useState, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

const FounderVideoSection = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const videoUrl = "https://customer-assets.emergentagent.com/job_preapproval-hub/artifacts/jxboybpx_AI%20Horsepower%20Unleashed%20-%20VEED%20Export-1772590590184.mp4";

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      }
    }
  };

  const handleVideoClick = () => {
    togglePlay();
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-4">
            Message From Our Founder
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            After You're <span className="text-emerald-400">Funded</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Hear directly from Roe Hubbard about what happens once you're approved and funded into our AI Horse-Powered Systems
          </p>
        </div>

        {/* Video Container with Green Border */}
        <div 
          className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20"
          style={{
            border: '4px solid',
            borderImage: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%) 1'
          }}
        >
          {/* Outer Glow Effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-600 rounded-2xl blur-lg opacity-30 -z-10"></div>
          
          {/* Video Player */}
          <div 
            className="relative bg-black aspect-video cursor-pointer group"
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => isPlaying && setShowControls(false)}
          >
            <video
              ref={videoRef}
              src={videoUrl}
              poster="https://customer-assets.emergentagent.com/job_preapproval-hub/artifacts/hz5j9n1x_image.png"
              className="w-full h-full object-cover"
              onClick={handleVideoClick}
              onEnded={handleVideoEnd}
              playsInline
              preload="metadata"
            />

            {/* Play Button Overlay - Shows when paused (Desktop only) */}
            {!isPlaying && (
              <div 
                className="absolute inset-0 hidden md:flex items-center justify-center bg-black/40 transition-opacity duration-300"
                onClick={handleVideoClick}
              >
                <button 
                  className="w-24 h-24 bg-emerald-500 hover:bg-emerald-400 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-lg shadow-emerald-500/50"
                  aria-label="Play video"
                >
                  <Play className="w-10 h-10 text-white ml-1" fill="white" />
                </button>
              </div>
            )}

            {/* Video Controls Bar */}
            <div 
              className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
                showControls ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={togglePlay}
                    className="w-10 h-10 bg-emerald-500/80 hover:bg-emerald-500 rounded-full flex items-center justify-center transition-colors"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-white" />
                    ) : (
                      <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                    )}
                  </button>
                  <button 
                    onClick={toggleMute}
                    className="w-10 h-10 bg-slate-700/80 hover:bg-slate-600 rounded-full flex items-center justify-center transition-colors"
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? (
                      <VolumeX className="w-5 h-5 text-white" />
                    ) : (
                      <Volume2 className="w-5 h-5 text-white" />
                    )}
                  </button>
                </div>
                <button 
                  onClick={handleFullscreen}
                  className="w-10 h-10 bg-slate-700/80 hover:bg-slate-600 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Fullscreen"
                >
                  <Maximize className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Green Accent Bar */}
          <div className="h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-600"></div>
        </div>

        {/* Founder Info Card */}
        <div className="mt-6 flex items-center justify-center gap-4 bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
            RH
          </div>
          <div>
            <p className="text-white font-semibold">Roe Hubbard</p>
            <p className="text-emerald-400 text-sm">Founder, One Force Live</p>
          </div>
        </div>

        {/* CTA Below Video */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 mb-4">Ready to get started on your path to funding?</p>
          <button 
            onClick={() => {
              const heroSection = document.getElementById('hero-section');
              if (heroSection) {
                heroSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50"
          >
            Get Pre-Approved Now
          </button>
        </div>
      </div>
    </section>
  );
};

export default FounderVideoSection;
